/**
 * Sign-in screen.
 *
 * Deliberately minimal and form-first: this is the entry point to an internal
 * operations workbench, so it states what the system does and what it needs rather
 * than acting as a marketing page. Credentials are sent straight to the API and are
 * never written to storage.
 */

import { useCallback, useEffect, useRef, useState, type FormEvent } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useLocation } from 'react-router-dom';

import { login } from '../api/client';
import { isApiError } from '../api/errors';
import { sessionStore, useSession } from '../app/useSession';
import { signOutNoticeFromState } from '../app/routerState';
import { Notice } from '../components/ui';

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

interface FieldErrors {
  email?: string;
  password?: string;
}

/**
 * A 401 on the login request means the credentials were rejected, not that an
 * existing session expired. Reporting it as "会话已过期" would send the user looking
 * for a problem that does not exist, so it is worded specifically here.
 */
function describeLoginFailure(error: unknown): { title: string; detail: string } {
  // Every message is written for the person signing in: no endpoint, configuration
  // variable or raw backend text appears on this public screen.
  if (!isApiError(error)) {
    return { title: '登录失败', detail: '发生了未预期的错误，请稍后重试。' };
  }
  switch (error.kind) {
    case 'unauthorized':
      return {
        title: '邮箱或密码不正确',
        detail: '请检查邮箱与密码后重试。连续失败可能触发账号锁定。',
      };
    case 'network':
      return {
        title: '无法连接服务',
        detail: '登录请求未能到达服务。请检查网络连接，或联系管理员确认服务已启动。',
      };
    case 'rate_limited':
      return { title: '尝试次数过多', detail: '请稍后再试。连续失败可能触发账号锁定。' };
    case 'forbidden':
      return { title: '账号暂时无法登录', detail: '账号可能已被停用或锁定，请联系租户管理员。' };
    case 'validation':
      return { title: '邮箱或密码格式不正确', detail: '请检查输入后重试。' };
    case 'server':
      return {
        title: '服务暂时不可用',
        detail:
          error.requestId === undefined
            ? '请稍后重试。'
            : `请稍后重试；如持续失败，请向管理员提供请求编号 ${error.requestId}。`,
      };
    case 'invalid_response':
      return { title: '登录响应异常', detail: '服务返回的数据不符合约定，请联系管理员。' };
    default:
      return { title: '登录失败', detail: '请稍后重试。' };
  }
}

export function LoginPage(): JSX.Element {
  const session = useSession();
  const location = useLocation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>();
  const [showPassword, setShowPassword] = useState(false);
  const emailRef = useRef<HTMLInputElement>(null);

  const signOutNotice = signOutNoticeFromState(location.state);

  useEffect(() => {
    emailRef.current?.focus();
  }, []);

  const mutation = useMutation({
    mutationFn: (input: { email: string; password: string }) => login(input),
    onSuccess: (result) => {
      // Store the CSRF token and profile; the session cookie is HttpOnly and is
      // set by the API itself.
      sessionStore.signIn({ csrfToken: result.csrf_token, user: result.user });
    },
  });

  const submit = useCallback(
    (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault();

      const errors: FieldErrors = {};
      const trimmedEmail = email.trim();
      if (trimmedEmail === '') errors.email = '请输入邮箱。';
      else if (!EMAIL_PATTERN.test(trimmedEmail)) errors.email = '邮箱格式不正确。';
      if (password === '') errors.password = '请输入密码。';

      setFieldErrors(errors);
      if (Object.keys(errors).length > 0) return;

      mutation.mutate({ email: trimmedEmail, password });
    },
    [email, mutation, password],
  );

  const failure = mutation.isError ? describeLoginFailure(mutation.error) : null;

  return (
    <div className="auth">
      <div className="auth__box">
        <div className="auth__brand">
          <span className="shell__brand-mark" aria-hidden="true">
            YQ
          </span>
          <h1 className="auth__title">舆情监测工作台</h1>
        </div>
        <p className="auth__sub">
          多租户监测与分析平台。登录后可管理监测主题、查看采集内容、处理告警与报告。
        </p>

        {signOutNotice !== null ? (
          <div style={{ marginBottom: 12 }}>
            <Notice tone="error" title="服务端退出失败" detail={signOutNotice} />
          </div>
        ) : null}

        {session.probeOutcome === 'unavailable' ? (
          <div style={{ marginBottom: 12 }}>
            <Notice
              tone="warn"
              title="无法确认既有登录状态"
              detail="启动时未能连接服务，因此需要重新登录。若服务尚未启动，登录请求也会失败。"
            />
          </div>
        ) : null}

        {failure !== null ? (
          <div style={{ marginBottom: 12 }}>
            <Notice tone="error" title={failure.title} detail={failure.detail} />
          </div>
        ) : null}

        <form className="auth__form" onSubmit={submit} noValidate>
          <div className="field">
            <label className="field__label" htmlFor="login-email">
              邮箱
            </label>
            <input
              id="login-email"
              ref={emailRef}
              type="email"
              name="email"
              value={email}
              autoComplete="username"
              spellCheck={false}
              aria-invalid={fieldErrors?.email !== undefined}
              aria-describedby={fieldErrors?.email !== undefined ? 'login-email-error' : undefined}
              onChange={(event) => {
                setEmail(event.target.value);
              }}
              disabled={mutation.isPending}
            />
            {fieldErrors?.email !== undefined ? (
              <span className="field__error" id="login-email-error" role="alert">
                {fieldErrors?.email}
              </span>
            ) : null}
          </div>

          <div className="field">
            <label className="field__label" htmlFor="login-password">
              密码
            </label>
            <div className="row row--tight" style={{ flexWrap: 'nowrap' }}>
              <input
                id="login-password"
                type={showPassword ? 'text' : 'password'}
                name="password"
                value={password}
                autoComplete="current-password"
                spellCheck={false}
                autoCapitalize="off"
                style={{ flex: 1 }}
                aria-invalid={fieldErrors?.password !== undefined}
                aria-describedby={
                  fieldErrors?.password !== undefined ? 'login-password-error' : undefined
                }
                onChange={(event) => {
                  setPassword(event.target.value);
                }}
                disabled={mutation.isPending}
              />
              {/* Named without “密码” so label lookups for the password field stay unique. */}
              <button
                type="button"
                className="btn btn--sm"
                aria-label={showPassword ? '隐藏输入内容' : '显示输入内容'}
                aria-controls="login-password"
                onClick={() => {
                  setShowPassword(!showPassword);
                }}
              >
                {showPassword ? '隐藏' : '显示'}
              </button>
            </div>
            {fieldErrors?.password !== undefined ? (
              <span className="field__error" id="login-password-error" role="alert">
                {fieldErrors?.password}
              </span>
            ) : null}
          </div>

          <button type="submit" className="btn btn--primary" disabled={mutation.isPending}>
            {mutation.isPending ? '登录中…' : '登录'}
          </button>
        </form>

        <div className="auth__note">
          <Notice
            tone="neutral"
            title="账号由管理员分配"
            detail="本工作台不提供自助注册。忘记密码请联系租户管理员重置。"
          />
        </div>
      </div>
    </div>
  );
}
